import { useMemo } from 'react';
import useSubscriptionStore from '@/store/useSubscriptionStore';
import { getTierFeatureQuota, LARGE_USAGE_LIMIT, type GatedFeature } from '@/constant/subscription';

interface FeatureGateResult {
  canUse: boolean;
  remaining: number;
  isGated: boolean;
  isSubscribed: boolean;
}

export function useFeatureGate(feature: GatedFeature): FeatureGateResult {
  const { subscription, quotas } = useSubscriptionStore();

  return useMemo(() => {
    const subscriptionTier = subscription?.tier ?? 'free';
    const maxQuota = getTierFeatureQuota(subscriptionTier, feature);
    const isGated = maxQuota !== Infinity;
    const isSubscribed = subscription?.status === 'active' || subscription?.status === 'past_due';

    if (isSubscribed || !isGated) {
      if (maxQuota >= LARGE_USAGE_LIMIT || maxQuota === Infinity) {
        return { canUse: true, remaining: Infinity, isGated, isSubscribed };
      }
    }

    const quota = quotas.find((q) => q.feature === feature);
    const now = new Date();
    const dayOfWeek = now.getUTCDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const thisMonday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + mondayOffset));
    const thisMondayStr = thisMonday.toISOString().slice(0, 10);

    let usedCount = 0;
    if (quota && quota.period_start >= thisMondayStr) {
      usedCount = quota.used_count;
    }

    const remaining = Math.max(0, maxQuota - usedCount);

    return {
      canUse: remaining > 0,
      remaining,
      isGated,
      isSubscribed,
    };
  }, [feature, subscription, quotas]);
}
